import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Box,
  Typography,
  Grid,
  Button,
  Chip,
  Paper,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { BatchProgress } from "../../services/emailService";
import { themeColors } from "../../utils/theme";

/**
 * Props for the BatchProgressModal component
 * 
 * @interface BatchProgressModalProps
 */
interface BatchProgressModalProps {
  /** Whether the modal is open/visible */
  show: boolean;
  /** Batch ID for tracking progress updates */
  batchId?: string;
  /** Real-time progress data for the batch from Firestore subscription */
  progress: BatchProgress | null;
  /** 
   * Callback invoked when batch completes successfully
   * Receives summary statistics including sent/failed counts and processing time
   */
  onComplete?: (summary: {
    sent: number;
    failed: number;
    total: number;
    processingTime: number;
  }) => void;
  /** Callback invoked when user manually closes the modal */
  onClose?: () => void;
}

/**
 * Props for the standalone progress badge component
 * 
 * @interface ProgressBadgeProps
 */
interface ProgressBadgeProps {
  /** Badge label text (e.g., "Sent", "Failed", "Total") */
  label: string;
  /** Numeric value to display */
  value: number;
  /** Color theme for badge styling */
  color: "green" | "red" | "gray";
  /** Optional click handler for interactive badges */
  onClick?: () => void;
  /** Whether the badge should show hover effects and be clickable */
  isClickable?: boolean;
}

/**
 * ProgressBadge Component
 * 
 * Displays a colored badge with a label and numeric value.
 * Supports optional click interactions with hover effects.
 * 
 * @component
 * @param {ProgressBadgeProps} props - Component props
 * @returns {JSX.Element} Styled progress badge
 * @example
 * <ProgressBadge label="Sent" value={42} color="green" />
 * <ProgressBadge 
 *   label="Failed" 
 *   value={3} 
 *   color="red" 
 *   isClickable 
 *   onClick={() => showFailedDetails()} 
 * />
 */
const ProgressBadge: React.FC<ProgressBadgeProps> = ({
  label,
  value,
  color,
  onClick,
  isClickable = false,
}) => {
  /**
   * Returns the background color for the badge based on color prop
   * @returns {string} CSS color value
   */
  const getBackgroundColor = () => {
    switch (color) {
      case "green":
        return "#e8f5e9";
      case "red":
        return "#ffebee";
      case "gray":
        return "#f5f5f5";
      default:
        return "#f5f5f5";
    }
  };

  /**
   * Returns the text color for the badge based on color prop
   * @returns {string} CSS color value
   */
  const getTextColor = () => {
    switch (color) {
      case "green":
        return "#2e7d32";
      case "red":
        return "#c62828";
      case "gray":
        return "#666";
      default:
        return "#666";
    }
  };

  return (
    <Box
      sx={{
        p: 1.5,
        backgroundColor: getBackgroundColor(),
        borderRadius: 1,
        textAlign: "center",
        cursor: isClickable ? "pointer" : "default",
        transition: "all 0.2s ease",
        "&:hover": isClickable ? {
          boxShadow: 2,
          transform: "translateY(-2px)",
        } : {},
      }}
      onClick={onClick}
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
    >
      <Typography variant="caption" sx={{ color: getTextColor() }}>
        {label}
      </Typography>
      <Typography
        variant="h6"
        sx={{ fontWeight: 600, color: getTextColor() }}
      >
        {value}
      </Typography>
    </Box>
  );
};

/**
 * BatchProgressModal Component
 *
 * Real-time progress monitoring modal for batch email sending operations.
 * 
 * Features:
 * - Live progress bar with percentage
 * - Color-coded status indicators (queued, in_progress, completed, failed)
 * - Interactive progress badges (sent, failed, total)
 * - Estimated time remaining calculation
 * - Auto-dismissing completion summary
 * - Failed email details with link to logs
 * - Prevents page navigation during processing
 * 
 * The component subscribes to Firestore updates via the progress prop
 * and automatically transitions from progress view to completion summary
 * when the batch finishes.
 * 
 * @component
 * @param {BatchProgressModalProps} props - Component props
 * @returns {JSX.Element | null} Modal dialog or null if not shown
 * @example
 * <BatchProgressModal
 *   show={isSending}
 *   batchId={currentBatchId}
 *   progress={batchProgress}
 *   onComplete={(summary) => {
 *     toast.success(`Sent ${summary.sent}/${summary.total} emails`);
 *   }}
 *   onClose={() => setIsSending(false)}
 * />
 */
export const BatchProgressModal: React.FC<BatchProgressModalProps> = ({
  show,
  batchId,
  progress,
  onComplete,
  onClose,
}) => {
  /** Controls visibility of failed emails detail alert */
  const [showFailedDetails, setShowFailedDetails] = useState(false);
  
  /** Controls visibility of the auto-dismissing completion modal */
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  
  /** Estimated time remaining display string (e.g., "2m 30s") */
  const [estimatedTimeRemaining, setEstimatedTimeRemaining] = useState<string | null>(null);
  
  /** Timestamp when batch processing started (for time calculations) */
  const startTimeRef = useRef<number | null>(null);
  
  /** Total elapsed processing time in milliseconds */
  const elapsedTimeRef = useRef<number>(0);

  /**
   * Effect: Initialize start time when progress begins
   * Records the timestamp when status changes to "in_progress"
   */
  useEffect(() => {
    if (show && progress && progress.status === "in_progress" && !startTimeRef.current) {
      startTimeRef.current = Date.now();
    }
  }, [show, progress]);

  /**
   * Effect: Calculate and update estimated time remaining
   * 
   * Runs every 500ms while batch is in progress.
   * Calculates processing rate based on elapsed time and processed items,
   * then estimates remaining time based on items left to process.
   */
  useEffect(() => {
    if (!show || !progress || !startTimeRef.current) {
      return;
    }

    const interval = setInterval(() => {
      if (progress.status !== "in_progress") {
        clearInterval(interval);
        return;
      }

      const elapsed = Date.now() - startTimeRef.current!;
      elapsedTimeRef.current = elapsed;
      const processed = progress.sent + progress.failed;

      if (processed > 0 && processed < progress.total) {
        // Calculate rate (items per millisecond)
        const rate = processed / elapsed;
        const remaining = progress.total - processed;
        const estimatedMs = remaining / rate;
        
        // Format time remaining as "X minutes Y seconds" or just "Xs"
        const minutes = Math.floor(estimatedMs / 60000);
        const seconds = Math.floor((estimatedMs % 60000) / 1000);
        
        if (minutes > 0) {
          setEstimatedTimeRemaining(`${minutes}m ${seconds}s`);
        } else if (seconds > 0) {
          setEstimatedTimeRemaining(`${seconds}s`);
        } else {
          setEstimatedTimeRemaining("< 1s");
        }
      }
    }, 500); // Update every 500ms

    return () => clearInterval(interval);
  }, [show, progress]);

  /**
   * Effect: Handle batch completion with auto-dismiss
   * 
   * When batch status changes to "completed":
   * 1. Shows completion summary modal
   * 2. Auto-dismisses after 3 seconds
   * 3. Invokes onComplete callback with summary stats
   * 4. Closes main modal via onClose callback
   */
  useEffect(() => {
    if (
      show &&
      progress &&
      progress.status === "completed" &&
      !showCompletionModal
    ) {
      // Show completion modal for 3 seconds then auto-dismiss
      setShowCompletionModal(true);
      
      const timer = setTimeout(() => {
        setShowCompletionModal(false);
        
        // Call onComplete callback with summary
        if (onComplete) {
          onComplete({
            sent: progress.sent,
            failed: progress.failed,
            total: progress.total,
            processingTime: elapsedTimeRef.current,
          });
        }
        
        // Close the main modal
        if (onClose) {
          onClose();
        }
      }, 3000); // Auto-dismiss after 3 seconds

      return () => clearTimeout(timer);
    }
  }, [show, progress, onComplete, onClose, showCompletionModal]);

  // Early return if modal should not be shown
  if (!show || !progress) {
    return null;
  }

  // Calculate percentage (ensure it doesn't exceed 100)
  const displayPercent = Math.min(progress.percent, 100);

  /**
   * Determines MUI color theme for status chip based on batch status
   * @returns {"success" | "error" | "info" | "warning" | "default"}
   */
  const getStatusColor = () => {
    switch (progress.status) {
      case "completed":
        return "success";
      case "failed":
        return "error";
      case "in_progress":
        return "info";
      case "queued":
        return "warning";
      default:
        return "default";
    }
  };

  /**
   * Determines human-readable label for status chip
   * @returns {string} Status label text
   */
  const getStatusLabel = () => {
    switch (progress.status) {
      case "completed":
        return "Completed";
      case "failed":
        return "Failed";
      case "in_progress":
        return "In Progress";
      case "queued":
        return "Queued";
      default:
        return "Processing";
    }
  };

  const isComplete = progress.status === "completed";
  const processedCount = progress.sent + progress.failed;

  /**
   * Completion Summary Modal
   * 
   * Separate modal that displays final batch statistics after completion.
   * Auto-dismisses after 3 seconds and triggers navigation to Email Logs.
   */
  const completionModal = showCompletionModal && isComplete && (
    <Dialog
      open={showCompletionModal}
      onClose={() => setShowCompletionModal(false)}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle sx={{ fontWeight: 600, pb: 1, display: "flex", alignItems: "center", gap: 1 }}>
        <CheckCircleIcon sx={{ color: "#2e7d32" }} />
        Batch Complete
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ textAlign: "center" }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            ✅ All emails processed successfully!
          </Typography>
          <Paper sx={{ p: 2, backgroundColor: "#f5f5f5", mb: 2 }}>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <Typography variant="body2" sx={{ color: "#666", mb: 0.5 }}>
                  Sent
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600, color: "#2e7d32" }}>
                  {progress.sent}/{progress.total}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" sx={{ color: "#666", mb: 0.5 }}>
                  Failed
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600, color: progress.failed > 0 ? "#c62828" : "#2e7d32" }}>
                  {progress.failed}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
          {progress.failed > 0 && (
            <Typography variant="body2" sx={{ color: "#c62828", mb: 2 }}>
              {progress.failed} email(s) failed. Check the Email Logs page for details.
            </Typography>
          )}
          <Typography variant="caption" sx={{ color: "#999" }}>
            Processing time: {(elapsedTimeRef.current / 1000).toFixed(1)}s
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={() => setShowCompletionModal(false)}
          variant="contained"
          sx={{ backgroundColor: themeColors.primary }}
        >
          View Email Logs
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <>
      {/* Main Progress Modal */}
      <Dialog
        open={show && !isComplete}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, pb: 1 }}>
          Sending Batch Emails
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          {/* Status chip */}
          <Box sx={{ mb: 2 }}>
            <Chip
              label={getStatusLabel()}
              color={getStatusColor()}
              size="small"
              variant="outlined"
            />
          </Box>

          {/* Progress bar */}
          <Box sx={{ mb: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mb: 1,
                alignItems: "center",
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                Progress
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: themeColors.primary }}>
                {displayPercent.toFixed(1)}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={displayPercent}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: "#e0e0e0",
                "& .MuiLinearProgress-bar": {
                  borderRadius: 4,
                  backgroundColor: themeColors.primary,
                },
              }}
            />
          </Box>

          {/* Progress Badges (8.2 requirement) */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 1.5 }}>
              Progress Badges
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <ProgressBadge
                  label="Sent"
                  value={progress.sent}
                  color="green"
                />
              </Grid>
              <Grid item xs={4}>
                <ProgressBadge
                  label="Failed"
                  value={progress.failed}
                  color="red"
                  isClickable={progress.failed > 0}
                  onClick={() => progress.failed > 0 && setShowFailedDetails(true)}
                />
              </Grid>
              <Grid item xs={4}>
                <ProgressBadge
                  label="Total"
                  value={progress.total}
                  color="gray"
                />
              </Grid>
            </Grid>
          </Box>

          {/* Estimated Time Remaining (8.3 requirement) */}
          {progress.status === "in_progress" && estimatedTimeRemaining && (
            <Box
              sx={{
                p: 1.5,
                backgroundColor: "#e3f2fd",
                borderRadius: 1,
                mb: 2,
              }}
            >
              <Typography variant="caption" sx={{ color: "#1565c0" }}>
                Estimated time remaining
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#1565c0" }}>
                {estimatedTimeRemaining}
              </Typography>
            </Box>
          )}

          {/* Failed Details Alert (if clicked) */}
          {showFailedDetails && progress.failed > 0 && (
            <Box
              sx={{
                p: 1.5,
                backgroundColor: "#ffebee",
                borderRadius: 1,
                mb: 2,
                borderLeft: `4px solid #c62828`,
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#c62828", mb: 0.5 }}>
                Failed Emails ({progress.failed})
              </Typography>
              <Typography variant="caption" sx={{ color: "#c62828" }}>
                Check the Email Logs page for detailed failure reasons and retry options.
              </Typography>
            </Box>
          )}

          {/* Processing message */}
          <Box
            sx={{
              p: 2,
              backgroundColor: "#f5f5f5",
              borderRadius: 1,
              textAlign: "center",
            }}
          >
            <Typography variant="body2" sx={{ color: "#666" }}>
              {processedCount} of {progress.total} emails processed
            </Typography>
            <Typography variant="caption" sx={{ color: "#999", mt: 0.5, display: "block" }}>
              {progress.total - processedCount} remaining
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Typography variant="body2" sx={{ color: "#666", flex: 1 }}>
            Processing... Keep this page open
          </Typography>
        </DialogActions>
      </Dialog>

      {/* Completion Summary Modal (8.3 requirement) */}
      {completionModal}
    </>
  );
};

export default BatchProgressModal;
